import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import FeatureNotifications from './FeatureNotifications';
import Breadcrumbs from './Breadcrumbs';
import ProductTour from './ProductTour';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Calendar,
  BookOpen,
  Sparkles,
  Users,
  BarChart3,
  Settings,
  LogOut,
  ChevronDown,
  Building2,
  UserCog,
  UserCheck,
  Wrench,
  Shield,
  TrendingUp,
  FileDown,
  Wallet,
  MessageSquare,
  BarChart2,
  Plus,
  Moon
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import LanguageSelector from './LanguageSelector';
import PropertyFormModal from './PropertyFormModal';
import api from '../services/api';

const Layout = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    fetchProperties();
    
    // Update date every minute to keep it current
    const dateInterval = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000); // Update every minute
    
    return () => clearInterval(dateInterval);
  }, []);

  // Auto-refresh data at midnight to update to new business day
  useEffect(() => {
    const checkAndRefresh = () => {
      const now = new Date();
      const nextMidnight = new Date();
      nextMidnight.setHours(24, 0, 0, 0); // Next midnight
      
      const msUntilMidnight = nextMidnight - now;
      
      // Set timeout to refresh at midnight
      const timeoutId = setTimeout(() => {
        console.log('[Layout] New day detected - refreshing data...');
        fetchProperties();
      }, msUntilMidnight);
      
      return timeoutId;
    };
    
    const timeoutId = checkAndRefresh();
    
    // Also refresh every 5 minutes to prevent stale data
    const refreshInterval = setInterval(() => {
      fetchProperties();
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => {
      clearTimeout(timeoutId);
      clearInterval(refreshInterval);
    };
  }, []);

  // Refresh data when user returns to tab after being away
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[Layout] Tab became visible - refreshing properties...');
        fetchProperties();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('access_token');

      if (!token) {
        console.log('[Layout] No token found - redirecting to login');
        navigate('/login');
        return;
      }

      console.log('[Layout] Fetching properties...');

      const response = await api.get('/properties');
      
      console.log('[Layout] Properties fetched:', response.data.length);
      console.log('[Layout] Properties:', response.data.map(p => p.name));
      
      setProperties(response.data);
      
      // If no properties exist, clear selected property
      if (response.data.length === 0) {
        console.log('[Layout] No properties found - clearing selection');
        setSelectedProperty(null);
        localStorage.removeItem('selected_property');
        return;
      }
      
      const saved = localStorage.getItem('selected_property');
      if (saved) {
        const savedProp = JSON.parse(saved);
        console.log('[Layout] Checking if saved property exists:', savedProp.name);
        const exists = response.data.find(p => p.id === savedProp.id);
        if (exists) {
          console.log('[Layout] Found saved property, setting as selected');
          setSelectedProperty(exists);
        } else if (response.data.length > 0) {
          console.log('[Layout] Saved property not found, selecting first available');
          setSelectedProperty(response.data[0]);
          localStorage.setItem('selected_property', JSON.stringify(response.data[0]));
        }
      } else if (response.data.length > 0) {
        console.log('[Layout] No saved property, selecting first');
        setSelectedProperty(response.data[0]);
        localStorage.setItem('selected_property', JSON.stringify(response.data[0]));
      }
    } catch (error) {
      console.error('[Layout] Error fetching properties:', error);
      console.error('[Layout] Error response:', error.response?.data);
      
      // If token expired (401), it will be handled by axios interceptor
      // For other errors, keep current state to avoid jarring UX
      if (error.response?.status === 401) {
        console.log('[Layout] 401 error - will be handled by global interceptor');
      } else if (error.response?.status === 403) {
        console.log('[Layout] 403 Forbidden - insufficient permissions');
      } else {
        console.log('[Layout] Network or other error - keeping current state');
      }
    }
  };

  const handlePropertyChange = (property) => {
    setSelectedProperty(property);
    localStorage.setItem('selected_property', JSON.stringify(property));
    localStorage.setItem('keyless.activePropertyId', property.id);
    // Navigate to same path to force child re-mount with new property
    navigate(location.pathname, { replace: true });
  };

  const handlePropertyCreated = (newProperty) => {
    fetchProperties();
    setSelectedProperty(newProperty);
    localStorage.setItem('selected_property', JSON.stringify(newProperty));
    localStorage.setItem('keyless.activePropertyId', newProperty.id);
    navigate(location.pathname, { replace: true });
  };


  const coreNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/front-desk', icon: UserCheck, label: t('nav.frontDesk') },
    { path: '/calendar', icon: Calendar, label: t('nav.calendar') },
    { path: '/reservations', icon: BookOpen, label: t('nav.reservations') },
    { path: '/guests', icon: Users, label: t('nav.guests') },
    { path: '/housekeeping', icon: Sparkles, label: t('nav.housekeeping') },
    { path: '/night-audit', icon: Moon, label: t('nav.nightAudit') },
    { path: '/reports', icon: BarChart3, label: t('nav.reports') },
    { path: '/settings', icon: Settings, label: t('nav.settings') },
  ];

  const moreNavItems = [
    { path: '/groups', icon: Users, label: t('nav.groups') },
    { path: '/rate-plans', icon: BarChart3, label: t('nav.ratePlans') },
    { path: '/channel-manager', icon: BarChart3, label: t('nav.channelManager') },
    { path: '/revenue-analytics', icon: BarChart3, label: t('nav.revenueAnalytics') },
    { path: '/addons', icon: BarChart3, label: t('nav.addons') },
    { path: '/promo-codes', icon: BarChart3, label: t('nav.promoCodes') },
    { path: '/staff', icon: UserCog, label: t('nav.staff') },
    { path: '/maintenance', icon: Wrench, label: t('nav.maintenance') },
    { path: '/audit-logs', icon: Shield, label: t('nav.auditLogs') },
    { path: '/forecasting', icon: TrendingUp, label: t('nav.forecasting') },
    { path: '/export-reports', icon: FileDown, label: t('nav.exportReports') },
    { path: '/accounting', icon: Wallet, label: t('nav.accounting') },
    { path: '/communications', icon: MessageSquare, label: t('nav.communications') },
    { path: '/advanced-analytics', icon: BarChart2, label: t('nav.advancedAnalytics') },
    { path: '/user-management', icon: UserCog, label: t('nav.userManagement') },
    { path: '/dnr', icon: Shield, label: t('nav.dnr') },
  ];

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-6 border-b border-slate-200">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
            Keyless PMS
          </h1>
          <p className="text-xs text-slate-500 mt-1">Property Management</p>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto">
          {/* Core Navigation */}
          <div className="space-y-1">
            {coreNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-3 py-3 rounded-lg text-base transition-colors ${
                  location.pathname === item.path
                    ? 'bg-gradient-to-r from-cyan-500/10 to-blue-500/10 text-cyan-700 font-semibold'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <item.icon size={22} />
                <span>{item.label}</span>
              </Link>
            ))}
          </div>

          {/* More Section */}
          <div className="mt-2 border-t border-slate-200 pt-2">
            <button
              onClick={() => setShowMore(!showMore)}
              className="flex items-center gap-3 px-3 py-3 w-full text-base text-slate-500 hover:text-slate-700 transition-colors"
            >
              <ChevronDown size={22} className={`transition-transform ${showMore ? 'rotate-180' : ''}`} />
              <span>{showMore ? t('common.less') || 'Less' : t('common.more') || 'More'}</span>
            </button>
            {showMore && (
              <div className="space-y-1 ml-2">
                {moreNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      location.pathname === item.path
                        ? 'bg-gradient-to-r from-cyan-500/10 to-blue-500/10 text-cyan-700 font-medium'
                        : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                    }`}
                  >
                    <item.icon size={18} />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Platform Admin (only for platform_admin role) */}
          {user?.role === 'platform_admin' && (
            <div className="mt-2 border-t border-slate-200 pt-2">
              <Link
                to="/platform-admin"
                className={`flex items-center gap-3 px-3 py-3 rounded-lg text-base transition-colors ${
                  location.pathname === '/platform-admin'
                    ? 'bg-gradient-to-r from-purple-500/10 to-pink-500/10 text-purple-700 font-semibold'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <Shield size={22} />
                <span>{t('nav.platformAdmin')}</span>
              </Link>
              <Link
                to="/database-admin"
                className={`flex items-center gap-3 px-3 py-3 rounded-lg text-base transition-colors ${
                  location.pathname === '/database-admin'
                    ? 'bg-gradient-to-r from-purple-500/10 to-pink-500/10 text-purple-700 font-semibold'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <Building2 size={22} />
                <span>{t('nav.databaseAdmin')}</span>
              </Link>
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-white font-semibold">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user.name}</p>
              <p className="text-xs text-slate-500 truncate">{user.email}</p>
            </div>
          </div>
          <Button
            onClick={() => {
              onLogout();
              navigate('/logged-out');
            }}
            variant="outline"
            data-testid="logout-button"
            className="w-full justify-start gap-2"
            size="sm"
          >
            <LogOut size={16} />
            {t('btn.logout')}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2 h-11 px-4" data-testid="property-selector">
                  <Building2 size={20} />
                  <span className="text-lg font-semibold">{selectedProperty ? selectedProperty.name : 'Select Property'}</span>
                  <ChevronDown size={18} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                {properties.map((property) => (
                  <DropdownMenuItem
                    key={property.id}
                    onClick={() => handlePropertyChange(property)}
                    data-testid={`property-option-${property.id}`}
                    className={selectedProperty?.id === property.id ? 'bg-slate-50' : ''}
                  >
                    <div>
                      <div className="font-medium">{property.name}</div>
                      <div className="text-xs text-slate-500">{[property.address, property.city, property.state, property.zip_code].filter(Boolean).join(', ') || 'No address'}</div>
                    </div>
                  </DropdownMenuItem>
                ))}
                
                {/* Add Property Option */}
                {properties.length > 0 && (
                  <div className="border-t border-slate-200 my-1"></div>
                )}
                <DropdownMenuItem
                  onClick={() => setShowAddPropertyModal(true)}
                  className="text-cyan-600 font-medium"
                >
                  <Plus size={16} className="mr-2" />
                  Add Property
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-3">
            <LanguageSelector />
            <div className="text-sm text-slate-600">
              {currentDate.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long',
                day: 'numeric' 
              })}
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 overflow-y-auto p-6">
          <FeatureNotifications />
          <Breadcrumbs />
          <Outlet key={selectedProperty?.id} context={{ selectedProperty, refreshProperties: fetchProperties, user }} />
        </div>
      </main>

      {/* Product Tour for new users */}
      <ProductTour />
      
      {/* Add Property Modal */}
      <PropertyFormModal
        open={showAddPropertyModal}
        onClose={() => setShowAddPropertyModal(false)}
        mode="create"
        onSuccess={handlePropertyCreated}
      />
    </div>
  );
};

export default Layout;
